/**
 * [#663] Portal session context — tenant identity + the real page-grant
 * matrix, read once for the whole portal section.
 *
 * The grant matrix comes from the SERVER (`GET /api/v1/portal/me`), never
 * from a role guess in the browser. It is used for VISIBILITY only: the rail
 * hides ungranted areas and the denied state explains an ungranted deep link,
 * while `requirePortalGrant` on every portal route remains the boundary.
 *
 * Four explicit states are exposed so every portal screen can render the four
 * states the design brief §5 requires without inventing its own: `loading`,
 * `error` (with a retry), `denied` (the server refused the session itself —
 * e.g. a non-canonical role), and `ready`.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  fetchPortalMe,
  isPortalAccessDenied,
  portalDeniedReason,
  type PortalDeniedReason,
  type PortalMe,
} from "../services/portal/portalApi";
import type { GrantMatrix } from "../portal/permissions/pageAreas";

export type PortalSessionStatus = "loading" | "ready" | "denied" | "error";

export interface PortalSessionValue {
  status: PortalSessionStatus;
  tenantId: string | null;
  tenantName: string | null;
  grants: GrantMatrix | null;
  /**
   * [#698] WHY the session was refused, when `status === "denied"`. The two
   * reasons need different user actions, so the shell must be able to tell
   * them apart rather than showing one explanation for both.
   * `/portal/me` carries no page grant of its own, so in practice this is
   * `role_denied` — but it is read from the server's envelope, not assumed.
   */
  deniedReason: PortalDeniedReason | null;
  errorMessage: string | null;
  reload: () => void;
}

const PortalSessionContext = createContext<PortalSessionValue | undefined>(
  undefined,
);

export interface PortalSessionProviderProps {
  children?: React.ReactNode;
  /** Test/storybook seam: inject a session instead of hitting the network. */
  initialSession?: PortalMe;
}

export const PortalSessionProvider: React.FC<PortalSessionProviderProps> = ({
  children,
  initialSession,
}) => {
  const [status, setStatus] = useState<PortalSessionStatus>(
    initialSession ? "ready" : "loading",
  );
  const [session, setSession] = useState<PortalMe | null>(
    initialSession ?? null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deniedReason, setDeniedReason] = useState<PortalDeniedReason | null>(
    null,
  );
  const [reloadToken, setReloadToken] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialSession) return undefined;
    // eslint-disable-next-line no-undef -- DOM lib global; core no-undef is not TS-aware
    const controller = new AbortController();
    setStatus("loading");
    setErrorMessage(null);
    setDeniedReason(null);
    fetchPortalMe(controller.signal)
      .then((me) => {
        if (!mounted.current) return;
        setSession(me);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (!mounted.current) return;
        if (error instanceof Error && error.name === "AbortError") return;
        if (isPortalAccessDenied(error)) {
          setSession(null);
          setDeniedReason(portalDeniedReason(error));
          setStatus("denied");
          return;
        }
        setErrorMessage(
          error instanceof Error ? error.message : "Portal session unavailable",
        );
        setStatus("error");
      });
    return () => controller.abort();
  }, [initialSession, reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const value = useMemo<PortalSessionValue>(
    () => ({
      status,
      tenantId: session?.tenantId ?? null,
      tenantName: session?.tenantName ?? null,
      grants: session?.grants ?? null,
      deniedReason,
      errorMessage,
      reload,
    }),
    [status, session, deniedReason, errorMessage, reload],
  );

  return (
    <PortalSessionContext.Provider value={value}>
      {children}
    </PortalSessionContext.Provider>
  );
};

export function usePortalSession(): PortalSessionValue {
  const value = useContext(PortalSessionContext);
  if (!value) {
    throw new Error(
      "usePortalSession must be used inside a PortalSessionProvider",
    );
  }
  return value;
}

export default PortalSessionContext;
